import { ApolloError } from '@apollo/client';
import { useNotificationContext } from '@modules/common/components/Notification/NotificationContext';
import { NOTIFICATION_TIMEOUT_LONG_8S } from '@modules/common/constants';
import { useIsIntegrationContextEnabled } from '@modules/integrationsV2/hooks';
import { getSuccessMessageForAddOrEditIntegration } from '@modules/integrationsV2/utils';
import {
  GetTenantByIdDocument,
  GetUserByEmailDocument,
  IntegrationChannelType,
  IntegrationType,
  ListIntegrationsDocument,
  UpdateIntegrationMutationVariables,
  useUpdateIntegrationMutation,
} from 'generated/graphql';
import { useCallback } from 'react';
import { apolloStatusToQueryStatus } from 'v2/core/repository/edge-server/statusConvertor';
import { useGetUser } from 'v2/core/services/user';

interface UseUpdateIntegrationOptions {
  channelType: IntegrationChannelType | undefined;
  integrationType: IntegrationType | undefined;
  onError?: (error: ApolloError) => void;
  onCompleted?: () => void;
  successNotificationContentNode?: JSX.Element;
}

export const useUpdateIntegration = ({
  channelType,
  integrationType,
  onError,
  onCompleted,
  successNotificationContentNode,
}: UseUpdateIntegrationOptions) => {
  const {
    tenant: { id: tenantID },
  } = useGetUser();

  const { showErrorNotification, showSuccessNotification } =
    useNotificationContext();

  const isIntegrationContextEnabled = useIsIntegrationContextEnabled();

  const [updateIntegrationMutation, { data, loading, error, called }] =
    useUpdateIntegrationMutation({
      onError: (error) => {
        if (onError) {
          onError(error);
          return;
        }
        showErrorNotification({
          contentComponent: `Something went wrong${
            error.message ? `: ${error.message}` : ''
          }`,
          props: {},
        });
      },
      onCompleted: () => {
        const successMessage =
          channelType && integrationType && !successNotificationContentNode
            ? getSuccessMessageForAddOrEditIntegration({
                channelType,
                integrationType,
                action: 'edit',
                isIntegrationContextEnabled,
              })
            : successNotificationContentNode;

        if (successMessage) {
          showSuccessNotification({
            contentComponent: () => successMessage,
            props: {
              isClosable: true,
              timeout: NOTIFICATION_TIMEOUT_LONG_8S,
            },
          });
        }

        onCompleted?.();
      },
      refetchQueries: [
        {
          query: GetTenantByIdDocument,
          variables: {
            tenantID,
          },
        },
        GetUserByEmailDocument,
        ListIntegrationsDocument,
      ],
      awaitRefetchQueries: true,
    });

  const status = apolloStatusToQueryStatus(loading, called, false, error);

  const updateIntegration = useCallback(
    async (input: UpdateIntegrationMutationVariables) =>
      await updateIntegrationMutation({ variables: input }),
    [updateIntegrationMutation],
  );

  return [updateIntegration, { data, status, error }] as const;
};
